import { Check, Close, Edit } from "@mui/icons-material";
import { Divider, Grid2 as Grid, IconButton, Typography } from "@mui/material";
import { FC } from "react";
import LabeledValue from "src/shared/components/LabeledValue";
import { preparePhoneForView } from "src/shared/helpers/phone.helpers";
import { renderLabeledValues } from "src/shared/helpers/render.helpers";
import { Can } from "src/shared/lib/rback/Can";
import { EAgencies } from "src/shared/types/common.types";
import { IPartnerDto } from "src/shared/types/partner.types";
import { BankAccount } from "./BankAccount";
import PartnerRegistrationDetails from "./PartnerRegistrationDetails";

interface IGeneralInfoProps {
  partner: IPartnerDto;
  handleOpenEditModal: (value: boolean) => void;
}
export const GeneralInfo: FC<IGeneralInfoProps> = ({
  partner,
  handleOpenEditModal,
}) => {
  const {
    agency,
    manager_main,
    organization_form,
    registration_country,
    is_resident,
    status,
    priority,
    is_nda_signed,
    nda_note,
    nda_file,
    edin_email,
    is_EDMS_enabled,
    phone,
    fax,
    state_registration_certificate,
  } = partner;

  const isWebcomRu = agency.value === EAgencies.Webcom_RU;
  const isByAgency =
    agency.value === EAgencies.Webcom_BY || agency.value === EAgencies.IContext;

  return (
    <Grid container spacing={4}>
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: "grey.500",
            fontSize: "1rem",
          }}
        >
          Основная информация
        </Typography>
        <Can perform={["partners.edit", "partners.edit.team"]}>
          <IconButton onClick={() => handleOpenEditModal(true)}>
            <Edit />
          </IconButton>
        </Can>
      </Grid>
      <Grid size={12}>
        <Typography
          component="span"
          sx={{
            color: "grey.500",
          }}
        >
          О компании
        </Typography>
      </Grid>
      {renderLabeledValues(
        [
          { label: "Компания", value: agency.name },
          {
            label: "Менеджер",
            value: `${manager_main.firstname} ${manager_main.lastname}`,
          },
          { label: "Вид деятельности", value: organization_form.name },
          {
            label: "Страна регистрации",
            value: registration_country.rus_country_name,
          },
          {
            label: "Резидент",
            isSuccess: is_resident,
          },
        ],
        { breakValues: true },
      )}
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={12}>
        <Typography
          component="span"
          sx={{
            color: "grey.500",
          }}
        >
          Сведения о регистрации
        </Typography>
      </Grid>
      <PartnerRegistrationDetails partner={partner} />
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={12}>
        <BankAccount
          bank_account={partner.bank_account}
          country_id={registration_country.id}
          is_resident={is_resident}
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={12}>
        <Typography
          component="span"
          sx={{
            color: "grey.500",
          }}
        >
          Описание клиента
        </Typography>
      </Grid>
      {renderLabeledValues(
        [
          { label: "Статус клиента", value: status.name },
          {
            label: "Категория",
            value: priority.name,
          },
          {
            label: "Подписан NDA",
            isSuccess: is_nda_signed,
          },
          {
            label: "Комментарий по NDA",
            value: is_nda_signed ? nda_note : undefined,
          },
          {
            label: "Скан NDA",
            value: is_nda_signed ? nda_file : undefined,
            isLink: is_nda_signed ? !!nda_file : undefined,
            linkTarget: nda_file,
          },
        ],
        { breakValues: true },
      )}
      <Grid size={12}>
        <Divider />
      </Grid>
      {isByAgency || isWebcomRu ? (
        <LabeledValue
          label={`Контрагент работает по ${isWebcomRu ? "Диадок" : "ЭДО"}`}
          value={
            is_EDMS_enabled ? (
              <Check color="success" />
            ) : (
              <Close color="error" />
            )
          }
        />
      ) : null}
      {is_EDMS_enabled && edin_email?.length ? (
        <LabeledValue
          label="ЭДО email"
          value={edin_email.map((email) => (
            <Typography
              component="span"
              key={email}
              sx={{
                wordBreak: "break-all",
                overflowWrap: "anywhere",
              }}
            >
              {email}
            </Typography>
          ))}
        />
      ) : null}
      {renderLabeledValues(
        [
          {
            label: "Телефон",
            value: preparePhoneForView(phone),
          },
          {
            label: "Факс",
            value: preparePhoneForView(fax),
          },
          {
            label: "Свидетельство ЮЛ",
            value: state_registration_certificate
              ? state_registration_certificate
              : undefined,
            isLink: state_registration_certificate
              ? !!state_registration_certificate
              : undefined,
            linkTarget: state_registration_certificate,
          },
        ],
        { breakValues: true },
      )}
    </Grid>
  );
};
